import { Fragment, ReactNode } from 'react';
import { styled, css } from 'styled-components';
import CheckboxField from './CheckboxField';

type InfoBannerVariant = 'blue' | 'orange';

interface InfoBannerProps {
	title: string;
	description: ReactNode;
	linkText?: string;
	onLinkClick?: () => void;
	variant?: InfoBannerVariant;
	checkboxLabel?: string;
	checkboxChecked?: boolean;
	onCheckboxChange?: (checked: boolean) => void;
	image?: {
		src: string;
		alt?: string;
		hideOnMobile?: boolean;
	};
}

const variantStyles = {
	blue: css`
		background: var(--Squla-Blue-10, #ddfcff);
		--banner-text-color: var(--Primary-squla-dark-blue, #0f0f41);
	`,
	orange: css`
		background: var(--Orange-10, #fff3dc);
		--banner-text-color: var(--Orange-100, #481e04);
	`,
};

const Container = styled.div<{ $variant: InfoBannerVariant }>`
	width: 100%;
	padding: 16px;
	border-radius: 8px;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	gap: 4px;
	display: inline-flex;

	${({ $variant }) => variantStyles[$variant]};

	@media screen and (min-width: 768px) {
		padding: 24px;
		border-radius: 12px;
		flex-direction: row;
		align-items: center;
		gap: 24px;
	}
`;

const MobileHeader = styled.div`
	align-self: stretch;
	justify-content: flex-start;
	align-items: center;
	gap: 16px;
	display: inline-flex;

	@media screen and (min-width: 768px) {
		display: none;
	}
`;

const MobileTitle = styled.div`
	flex: 1 1 0;
	color: var(--banner-text-color);
	font-size: 14px;
	font-weight: 800;
	line-height: 16px;
	word-wrap: break-word;
`;

const MobileImageWrapper = styled.img`
	width: 40px;
	height: 33px;
	position: relative;
	overflow: hidden;
	flex-shrink: 0;
`;

const ContentWrapper = styled.div`
	flex: 1 1 0;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 16px;
	display: inline-flex;
`;

const TextContent = styled.div`
	align-self: stretch;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 4px;
	display: flex;
`;

const Title = styled.div`
	display: none;
	align-self: stretch;
	color: var(--banner-text-color);
	font-size: 16px;
	font-weight: 800;
	line-height: 20px;
	word-wrap: break-word;

	@media screen and (min-width: 768px) {
		display: block;
	}
`;

const Description = styled.div`
	align-self: stretch;
	color: var(--banner-text-color);
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
	word-wrap: break-word;
`;

const LinkText = styled.span`
	color: var(--banner-text-color);
	font-size: 14px;
	font-weight: 400;
	text-decoration: underline;
	line-height: 20px;
	cursor: pointer;
	display: inline;

	&:hover {
		text-decoration-thickness: 2px;
	}
`;

const StyledCheckboxField = styled(CheckboxField)`
	align-self: stretch;
	color: var(--banner-text-color);
`;

const DesktopImageWrapper = styled.img`
	display: none;
	flex-shrink: 0;
	max-width: 84px;
	max-height: 84px;

	@media screen and (min-width: 768px) {
		display: block;
	}
`;

export function InfoBanner({
	title,
	description,
	linkText,
	onLinkClick,
	variant = 'blue',
	checkboxLabel,
	checkboxChecked = false,
	onCheckboxChange,
	image,
}: InfoBannerProps) {
	const handleLinkClick = () => {
		onLinkClick?.();
	};

	const handleCheckboxChange = (checked: boolean) => {
		onCheckboxChange?.(checked);
	};

	return (
		<Container $variant={variant}>
			{/* Mobile header with title and image */}
			<MobileHeader>
				<MobileTitle>{title}</MobileTitle>
				{image && !image.hideOnMobile && <MobileImageWrapper src={image.src} alt={image.alt} />}
			</MobileHeader>

			<ContentWrapper>
				<TextContent>
					{/* Desktop title */}
					<Title>{title}</Title>
					<Description>
						<span>{description}</span>
						{linkText && (
							<Fragment>
								{' '}
								<LinkText onClick={handleLinkClick} role="button" tabIndex={0}>
									{linkText}
								</LinkText>
							</Fragment>
						)}
					</Description>
				</TextContent>

				{checkboxLabel && (
					<StyledCheckboxField
						label={checkboxLabel}
						checked={checkboxChecked}
						onChange={handleCheckboxChange}
					/>
				)}
			</ContentWrapper>

			{/* Desktop image */}
			{image && <DesktopImageWrapper src={image.src} alt={image.alt} />}
		</Container>
	);
}

export type { InfoBannerProps, InfoBannerVariant };
export default InfoBanner;
